import { subscriptionApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  SubscriptionI,
  CreateSubscriptionI,
  SubscriptionPaymentI,
  GetSubscriptionParamsI,
  ServiceResponseI,
  DataI,
  RegisterPaymentSubscriptionI,
  ChangePlanSubscriptionI,
} from "@interfaces";
import { AxiosError } from "axios";

const token = () => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  return t;
};

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error).message);
};

const getSubscriptions = async ({
  page,
  limit,
  search,
  sorting,
}: GetSubscriptionParamsI): Promise<ServiceResponseI<DataI<SubscriptionI>>> => {
  try {
    const params = new URLSearchParams();
    params.append("page", Number(page ?? 1).toString());
    params.append("limit", Number(limit ?? 15).toString());
    params.append("search", search ?? "");
    params.append("sorting", sorting ?? JSON.stringify([]));

    const { data } = await subscriptionApi.get<
      ServiceResponseI<DataI<SubscriptionI>>
    >("", {
      params,
      headers: { Authorization: token() },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionById = async (
  id: string,
): Promise<ServiceResponseI<SubscriptionI>> => {
  try {
    const { data } = await subscriptionApi.get<ServiceResponseI<SubscriptionI>>(
      `/${id}`,
      {
        headers: { Authorization: token() },
      },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const getSubscriptionPayments = async (
  id: string,
): Promise<ServiceResponseI<SubscriptionPaymentI[]>> => {
  try {
    const { data } = await subscriptionApi.get<
      ServiceResponseI<SubscriptionPaymentI[]>
    >(`/${id}/payments`, { headers: { Authorization: token() } });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const getSubscriptionsByClient = async (
  clientId: string,
): Promise<ServiceResponseI<DataI<SubscriptionI[]>>> => {
  try {
    const { data } = await subscriptionApi.get<
      ServiceResponseI<DataI<SubscriptionI[]>>
    >(`/by-client/${clientId}`, { headers: { Authorization: token() } });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionPost = async (
  payload: CreateSubscriptionI,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.post<ServiceResponseI<string>>(
      "",
      payload,
      {
        headers: { Authorization: token() },
      },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionPut = async (
  id: string,
  payload: CreateSubscriptionI,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      {
        headers: { Authorization: token() },
      },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionRenew = async (
  id: string,
  notes?: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.patch<ServiceResponseI<string>>(
      `/renew/${id}`,
      { notes: notes ?? null },
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionChangePlan = async (
  id: string,
  payload: ChangePlanSubscriptionI,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.patch<ServiceResponseI<string>>(
      `/change-plan/${id}`,
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionSuspend = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.patch<ServiceResponseI<string>>(
      `/suspend/${id}`,
      {},
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionRegisterPayment = async (
  payload: RegisterPaymentSubscriptionI,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.post<ServiceResponseI<string>>(
      "/register-payment",
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const subscriptionDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: token() },
      },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useSubscriptions = ({
  page,
  limit,
  search,
  sorting,
}: GetSubscriptionParamsI) => {
  const subscriptionsQuery = useQuery(
    ["subscriptions", { page, limit, search, sorting }],
    () => getSubscriptions({ page, limit, search, sorting }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { subscriptionsQuery };
};

export const subscriptionActivate = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await subscriptionApi.patch<ServiceResponseI<string>>(
      `/activate/${id}`,
      {},
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useClientSubscriptions = (clientId: string | null) => {
  const clientSubsQuery = useQuery(
    ["subscriptions-client", clientId],
    () => getSubscriptionsByClient(clientId!),
    { enabled: !!clientId, refetchOnWindowFocus: false },
  );
  return { clientSubsQuery };
};

export const useSubscriptionPayments = (id: string | null) => {
  const paymentsQuery = useQuery(
    ["subscription-payments", id],
    () => getSubscriptionPayments(id!),
    { enabled: !!id, refetchOnWindowFocus: false },
  );
  return { paymentsQuery };
};
